'use client';
import React, { useState } from 'react';
import { Search, ArrowRight, ChevronLeft } from 'lucide-react';
import type { WizardData } from './VisaWizard';

interface Props {
  data: WizardData;
  updateData: (d: Partial<WizardData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const popularCountries = [
  { name: 'United States', flag: '🇺🇸', code: 'US', visaRequired: true },
  { name: 'United Kingdom', flag: '🇬🇧', code: 'GB', visaRequired: true },
  { name: 'Germany', flag: '🇩🇪', code: 'DE', visaRequired: true },
  { name: 'France', flag: '🇫🇷', code: 'FR', visaRequired: true },
  { name: 'Canada', flag: '🇨🇦', code: 'CA', visaRequired: true },
  { name: 'Australia', flag: '🇦🇺', code: 'AU', visaRequired: true },
  { name: 'Japan', flag: '🇯🇵', code: 'JP', visaRequired: true },
  { name: 'UAE', flag: '🇦🇪', code: 'AE', visaRequired: true },
  { name: 'Netherlands', flag: '🇳🇱', code: 'NL', visaRequired: true },
  { name: 'Italy', flag: '🇮🇹', code: 'IT', visaRequired: true },
  { name: 'Spain', flag: '🇪🇸', code: 'ES', visaRequired: true },
  { name: 'New Zealand', flag: '🇳🇿', code: 'NZ', visaRequired: true },
];

const visaTypes = [
  { id: 'tourist', emoji: '🏖️', label: 'Tourist / Visitor', sub: 'Type B, Type C, ETA', duration: 'Up to 90 days' },
  { id: 'student', emoji: '🎓', label: 'Student', sub: 'Tier 4, F-1, Study Permit', duration: 'Duration of study' },
  { id: 'work', emoji: '💼', label: 'Work', sub: 'H-1B, Tier 2, Work Permit', duration: 'Per contract' },
  { id: 'business', emoji: '🤝', label: 'Business', sub: 'Type B-1, Business Short Stay', duration: 'Up to 90 days' },
  { id: 'transit', emoji: '✈️', label: 'Transit', sub: 'Airport Transit, DATV', duration: 'Up to 48 hours' },
  { id: 'family', emoji: '👨‍👩‍👧', label: 'Family Reunion', sub: 'Spouse, Child, Parent visa', duration: '1–5 years' },
];

export default function Step2VisaSelection({ data, updateData, onNext, onBack }: Props) {
  const [search, setSearch] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<{ name: string; flag: string } | null>(
    data.destinationCountry ? { name: data.destinationCountry, flag: data.destinationFlag } : null
  );
  const [selectedVisa, setSelectedVisa] = useState(data.visaType || '');

  const filtered = popularCountries.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  const canContinue = selectedCountry !== null && selectedVisa !== '';

  const handleContinue = () => {
    if (!selectedCountry) return;
    updateData({
      destinationCountry: selectedCountry.name,
      destinationFlag: selectedCountry.flag,
      visaType: selectedVisa,
    });
    onNext();
  };

  return (
    <div className="wizard-card space-y-7 animate-slide-up">
      {/* Header */}
      <div className="space-y-1">
        <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest">Step 2 of 9</p>
        <h2 className="text-xl font-extrabold text-gray-900 tracking-tight">Where are you applying to?</h2>
        <p className="text-sm text-gray-500">Select your destination country and the type of visa you need.</p>
      </div>

      {/* Country search */}
      <div className="space-y-3">
        <label className="text-sm font-bold text-gray-700">Destination country</label>
        <div className="relative">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search 180+ countries..."
            className="input-field pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Country grid */}
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-56 overflow-y-auto pr-1">
          {filtered.map((country) => (
            <button
              key={`country-${country.code}`}
              onClick={() => setSelectedCountry({ name: country.name, flag: country.flag })}
              className={`flex flex-col items-center gap-1 p-2.5 rounded-xl border text-center transition-all duration-150 ${
                selectedCountry?.name === country.name
                  ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-100' :'border-gray-200 bg-white hover:border-blue-200 hover:bg-blue-50/20'
              }`}
            >
              <span className="text-xl">{country.flag}</span>
              <span className={`text-xs font-semibold leading-tight ${
                selectedCountry?.name === country.name ? 'text-blue-700' : 'text-gray-700'
              }`}>{country.name}</span>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-4 py-6 text-center text-sm text-gray-400">
              No country found for &ldquo;{search}&rdquo; — try a different spelling
            </div>
          )}
        </div>
      </div>

      {/* Visa type */}
      <div className="space-y-3">
        <label className="text-sm font-bold text-gray-700">Visa type</label>
        <div className="grid sm:grid-cols-2 gap-2.5">
          {visaTypes.map((vt) => (
            <button
              key={`visa-type-${vt.id}`}
              onClick={() => setSelectedVisa(vt.id)}
              className={`flex items-center gap-3 p-3.5 rounded-xl border text-left transition-all duration-150 ${
                selectedVisa === vt.id
                  ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-100' :'border-gray-200 bg-white hover:border-blue-200 hover:bg-blue-50/20'
              }`}
            >
              <span className="text-xl shrink-0">{vt.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-bold ${selectedVisa === vt.id ? 'text-blue-800' : 'text-gray-800'}`}>
                  {vt.label}
                </p>
                <p className="text-xs text-gray-400 truncate">{vt.sub}</p>
              </div>
              <span className="text-xs text-gray-400 shrink-0">{vt.duration}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Selected summary */}
      {selectedCountry && selectedVisa && (
        <div className="flex items-center gap-3 p-3.5 bg-green-50 border border-green-100 rounded-xl animate-fade-in">
          <span className="text-2xl">{selectedCountry.flag}</span>
          <div>
            <p className="text-sm font-bold text-green-800">
              {selectedCountry.name} — {visaTypes.find((v) => v.id === selectedVisa)?.label}
            </p>
            <p className="text-xs text-green-600">AI will generate requirements for this combination</p>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center gap-3 pt-2">
        <button onClick={onBack} className="btn-secondary py-2.5 px-4 text-sm">
          <ChevronLeft size={15} />
          Back
        </button>
        <button
          onClick={handleContinue}
          disabled={!canContinue}
          className="btn-primary flex-1 justify-center py-2.5 text-sm"
        >
          Continue to personal info
          <ArrowRight size={15} />
        </button>
      </div>
    </div>
  );
}